import { destroyCookie, parseCookies, setCookie } from 'nookies'
import { Cookies } from '@/client/types'

// Shop default currency, baked in per shop build via NEXT_PUBLIC_DEFAULT_CURRENCY.
export const DEFAULT_CURRENCY = process.env.NEXT_PUBLIC_DEFAULT_CURRENCY || 'EUR'

// Locales that are allowed to switch the calculator/cart to a currency other
// than the shop default, mapped to that alternative currency.
// Only the UK shop (default currency GBP) supports this today.
// Not clear yet whether the locale key will be 'gb' or 'uk', so both are mapped
// (mirrors the same uncertainty handled for LOCALE_TO_SHOP_DOMAIN).
export const ALTERNATIVE_CURRENCIES: Record<string, string> = {
  uk: 'EUR'
}

/**
 * Returns the alternative currency a user may switch to for the given locale,
 * or undefined if this shop only supports its single default currency.
 */
export const getAlternativeCurrency = (locale?: string): string | undefined =>
  locale ? ALTERNATIVE_CURRENCIES[locale] : undefined

// The price/cart API doesn't take a currency code in the payload — it takes
// the numeric id it stores in the DB for the currency.
export const CURRENCY_IDS: Record<string, number> = {
  EUR: 1,
  GBP: 2,
  USD: 3,
  AUD: 4
}

const CURRENCY_CODES_BY_ID: Record<number, string> = Object.fromEntries(
  Object.entries(CURRENCY_IDS).map(([code, id]) => [id, code])
)

export const getCurrencyId = (currencyCode?: string): number | undefined =>
  currencyCode ? CURRENCY_IDS[currencyCode] : undefined

export const getCurrencyCodeById = (currencyId?: number): string | undefined =>
  currencyId !== undefined ? CURRENCY_CODES_BY_ID[currencyId] : undefined

/**
 * The cart document itself always carries a `currency_id` (set once, from
 * the first item added) — this is the authoritative currency for everything
 * in that cart, since a cart can only ever hold one currency.
 */
export const getCartCurrency = (cart?: { currency_id?: number }): string =>
  getCurrencyCodeById(cart?.currency_id) || DEFAULT_CURRENCY

/**
 * A cart item may carry its own `currency_id` too, but it isn't always
 * populated by the API on every item. Prefer it when present, otherwise fall
 * back to the cart's own currency_id (always set), then the shop default.
 */
export const getCartItemCurrency = (item?: { currency_id?: number }, cart?: { currency_id?: number }): string =>
  getCurrencyCodeById(item?.currency_id) || getCartCurrency(cart)

/**
 * Once a user switches to a non-default currency, that choice should keep
 * being used for every new calculation (new calculator session, reset,
 * starting another order, ...) until they explicitly switch back — it's a
 * sticky preference, not something tied to a single selection object. It's
 * persisted in a cookie (like the cart id) so it survives across page loads.
 */
export const getPreferredCurrency = (): string => {
  const cookies = parseCookies()
  return cookies[Cookies.CURRENCY] || DEFAULT_CURRENCY
}

export const setPreferredCurrency = (currency: string): void => {
  if (currency === DEFAULT_CURRENCY) {
    destroyCookie(null, Cookies.CURRENCY, { path: '/' })
  } else {
    setCookie(null, Cookies.CURRENCY, currency, { maxAge: 30 * 24 * 60 * 60, path: '/' })
  }
}

/**
 * The currency actually in force for the current shopping session.
 *
 * Once the cart holds items, its `currency_id` is authoritative — a cart can
 * only ever hold one currency, and switching is disabled everywhere in the UI
 * while it has items — so every new calculation must be priced in that same
 * currency, no matter what the (possibly stale, e.g. left over from before
 * the cart was started, or never set) currency cookie says. Only an empty
 * cart defers to the user's sticky cookie preference, or the shop default.
 */
export const getEffectiveCurrency = (cart?: { currency_id?: number; cart_items?: unknown[] }): string =>
  cart?.cart_items?.length ? getCartCurrency(cart) : getPreferredCurrency()

/**
 * Applies the currency that should govern a freshly built selection (e.g. a
 * new calculator session or a reset) — the cart's currency when it already
 * has items, otherwise the user's sticky preference — so new calculations
 * keep using the right currency instead of silently falling back to the shop
 * default. The API expects the numeric `currency_id`, not a code.
 */
export const applyPreferredCurrency = <T extends Record<string, unknown>>(
  selection: T,
  cart?: { currency_id?: number; cart_items?: unknown[] }
): T => {
  const preferred = getEffectiveCurrency(cart)

  if (preferred === DEFAULT_CURRENCY) {
    const newSelection = { ...selection }
    delete newSelection.currency_id
    return newSelection
  }

  return { ...selection, currency_id: getCurrencyId(preferred) }
}
